import type { FreezeField, FreezeView, NextAction, RefusedStep } from "../api/types.js";
import { refusalWords } from "./bank-words.js";
import type { SurfaceOpeners } from "./surfaces.js";
import type { OfferTarget } from "../ui/chat.js";

/**
 * The words on 07's stops (UX spec v3 §6.7, copy §8.1).
 *
 * The freeze shows what was said beside what was about to be sent and never why
 * they differ. Both readings are the BANK's (`fields[]` on the freeze, DCTA-74
 * run 3 ask v): the customer's words as the resolver re-anchored them, and the
 * drafter's reading of them. The client writes no reading of its own — when the
 * bank names no field (the stub validator has none), the card stops without a
 * pair rather than quoting the client's own draft back as if it were the bank's.
 */
interface DraftStep {
  readonly step_id?: string;
  readonly type?: string;
}

export interface Pair {
  readonly said: string;
  readonly about: string;
}

export function freezePairs(fields: readonly FreezeField[] | undefined, draft: unknown): Pair[] {
  const steps = (draft as { steps?: readonly DraftStep[] } | null)?.steps ?? [];
  return (fields ?? []).map((f) => {
    const cp = f.counterparty;
    if (cp === undefined) return { said: f.said, about: f.drafter_reading };
    // The joining word is the step's type (the draft the words came with): an order is "of", anything else "to".
    const joint = steps.find((s) => s.step_id === f.step_id)?.type === "securities_order" ? "of" : "to";
    // QA D7 (DCTA-170): the bank's words may already name the payee (the resolver anchored the whole sentence) — then they
    // stand as said, never "…for John Doe too to John Doe".
    const named = f.said.toLowerCase().includes(cp.said.toLowerCase());
    return { said: named ? f.said : `${f.said} ${joint} ${cp.said}`, about: `${f.drafter_reading} ${joint} ${cp.drafter_reading}` };
  });
}

/** The freeze codes: two readings of the same words disagreed, so nothing was drafted to sign. */
/**
 * The reason code beside an error (DCTA-85 P2-2; DCTA-120, review P1-3). No customer surface ever
 * shows a reason code (§F.2: `SERVICE_UNAVAILABLE`, `INVALID_REQUEST`, `OFFLINE` — none of them is a
 * reference a customer can use); judge mode (the jury's view of the machinery) shows it, under a
 * `.judge` root. A refusal's RULE id (`POL-*`, `FW-*`) is another thing: a reference, on refusal
 * cards only (`refusalCard`).
 */
export function errorCode(code: string, judge: boolean): string | undefined {
  return judge ? code : undefined;
}

export const FREEZE_CODES: ReadonlySet<string> = new Set(["VALIDATOR_MISMATCH", "AMOUNT_VALUE_MISMATCH", "DRAFT_SPAN_MISMATCH"]);

/** What the assistant says for a stop the bank made (DCTA-124): its sentences, its ways on, a rule id for judge mode. */
export interface Message {
  readonly lines: readonly string[];
  /** Judge mode only (Umakhan's ruling, 5 Oct): never on a customer screen. */
  readonly rule?: string;
  /** `asks` (DCTA-226, §5.7): the bank's offer the chip answers by id — words that ask while it is open go there. */
  readonly acts: readonly { readonly label: string; readonly once?: boolean; readonly onSelect: () => void; readonly asks?: OfferTarget }[];
  /**
   * DCTA-189 (QA DW-2): these ways on stand through a read-only turn — the identity check's "Update my details" (support,
   * topic `kyc_refresh`) is still true after "What's my balance?": money still can't move. A new request retires them.
   */
  readonly standing?: boolean;
}

/**
 * A refused step as the assistant's message (DCTA-124 — Umakhan's ruling, 5 Oct: the Bank surface is only
 * the card the customer signs and its result). The bank's sentence and its first next step, verbatim
 * (`refusalWords`; screen = record), and its ways on as chips, in the bank's order, when a handler can act on
 * them: the bank's offer (used once, answered by id; ask u), Talk to us, and — DCTA-154 (F26, §F.2 21s) — one
 * of the bank's own screens, only when the app can open it (`openers`: never a chip that does nothing). A cap
 * refusal sends "See my limits" and, after B34, "Talk to us" too: both are drawn, both usable again. A refusal
 * read back as history has none. DCTA-75 P1-3: a next step that only repeats its offer's words is said once — by
 * the chip.
 */
export function refusalMessage(r: RefusedStep, act?: (action: NextAction, label: string) => void, openers: SurfaceOpeners = {}): Message {
  const w = refusalWords(r);
  const acts: Message["acts"] =
    act === undefined
      ? []
      : (r.next_actions ?? []).flatMap(({ label, action }) =>
          action.kind === "bank_surface"
            ? openers[action.surface] === undefined
              ? []
              : [{ label, onSelect: () => act(action, label) }]
            : action.kind === "answer"
              ? [{ label, once: true, asks: { draftId: action.draft_id, questionId: action.question_id }, onSelect: () => act(action, label) }]
              : [{ label, onSelect: () => act(action, label) }],
        );
  const bare = (t: string): string => t.trim().replace(/[.!?]+$/, "").trim();
  const next = w.next?.trim();
  const repeated = next !== undefined && acts.some((a) => a.once === true && bare(next) === bare(a.label));
  const lines = next === undefined || next === "" || repeated ? [w.lead] : [w.lead, /[.!?]$/.test(next) ? next : `${next}.`];
  const standing = acts.length > 0 && (r.next_actions ?? []).some(({ action }) => action.kind === "support" && action.topic === "kyc_refresh");
  return { lines, ...(w.rule === undefined ? {} : { rule: w.rule }), acts, standing };
}

/** §F.2 11q's words, for a bank that sent none (the bank sends them at source since DCTA-123). */
const FREEZE_LEAD = "What you said and what was about to be sent don't match, so nothing was sent.";
const FREEZE_NEXT = "Say it again, or type it, and check the card before you sign.";

export interface FreezeMessage {
  readonly lines: readonly string[];
  readonly pairs: readonly Pair[];
  /** Say it again (with a microphone) · Type it. */
  readonly acts: readonly ("say" | "type")[];
}

/**
 * DCTA-188: the bank's freeze sentence names both values ("I stopped this: you said S$100.00 to Mum, but the draft had
 * S$1,000.00. Nothing was sent.") — only when its `freeze` block says so, in exactly those words.
 */
export function namesBothValues(freeze: unknown): freeze is FreezeView & { readonly rendered: "values" } {
  return typeof freeze === "object" && freeze !== null && (freeze as { rendered?: unknown }).rendered === "values";
}

/**
 * The freeze as the assistant's message (§6.7, 11q; DCTA-124): the bank's sentence and next step verbatim,
 * the two readings side by side (the bank's `fields[]`, never the client's own), and two ways to say it again.
 * DCTA-188 Change 1: a sentence that names both values (`freeze.rendered: "values"`) is drawn ALONE — it already says
 * what was said and what the draft had, so no next-step line and no pairs; the two chips stay. A field-only sentence, or
 * a bank that sends no `freeze` (older, `DRAFT_SPAN_MISMATCH`), is drawn as before.
 */
export function freezeMessage(
  body: { readonly message?: unknown; readonly next_steps?: unknown; readonly fields?: readonly FreezeField[] | undefined; readonly freeze?: unknown },
  draft: unknown,
  voice: boolean,
): FreezeMessage {
  const lead = typeof body.message === "string" && body.message !== "" ? body.message : FREEZE_LEAD;
  const acts: FreezeMessage["acts"] = voice ? ["say", "type"] : ["type"];
  if (namesBothValues(body.freeze) && lead !== FREEZE_LEAD) return { lines: [lead], pairs: [], acts };
  const steps = Array.isArray(body.next_steps) ? (body.next_steps as unknown[]) : [];
  const next = typeof steps[0] === "string" && steps[0] !== "" ? steps[0] : FREEZE_NEXT;
  return { lines: [lead, next], pairs: freezePairs(body.fields, draft), acts };
}
